import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SubAccountLayout from '../../components/SubAccountLayout';
import { useSubAccount } from '../../components/SubAccountLayout';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import {
  Phone,
  Plus,
  Search,
  Settings,
  Trash2,
  CheckCircle,
  XCircle,
  X,
  Loader2,
  Bot,
  Globe,
  MessageSquare,
  Mic,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  Link2,
  MoreVertical,
  Edit3,
  Copy,
  Download,
  Check,
  Square,
  CheckSquare,
  DollarSign,
  AlertTriangle,
  Unplug
} from 'lucide-react';

const PhoneNumbersContent = () => {
  const { workspaceId, subAccountId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { subAccount } = useSubAccount();
  const { isDarkMode } = useTheme();
  
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Provider connection state
  const [twilioConnected, setTwilioConnected] = useState(false);
  const [telnyxConnected, setTelnyxConnected] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(null); // 'twilio' or 'telnyx'
  const [twilioBalance, setTwilioBalance] = useState(null);
  const [telnyxBalance, setTelnyxBalance] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [dismissedBanner, setDismissedBanner] = useState(() => {
    const dismissed = localStorage.getItem('vox_balance_banner_dismissed');
    if (dismissed) {
      const elapsed = Date.now() - parseInt(dismissed, 10);
      return elapsed < 3600000; // 1 hour cooldown
    }
    return false;
  });
  const [providerMenu, setProviderMenu] = useState(null); // 'twilio' | 'telnyx' | null
  const [checkingBalance, setCheckingBalance] = useState(null);

  // Disconnect provider confirmation (requires typing CONFIRM)
  const [disconnectConfirm, setDisconnectConfirm] = useState({ provider: null, text: '' });

  // Delete phone number confirmation
  const [deleteNumberConfirm, setDeleteNumberConfirm] = useState(null); // phone object or null
  
  // Connect form state
  const [connectForm, setConnectForm] = useState({
    account_sid: '',
    auth_token: '',
    api_key: '',
    parent_account_sid: '',     // Twilio subaccount support: optional parent creds
    parent_auth_token: '',
  });
  // Twilio Advanced section: open by default when subaccount auto-detected
  const [parentCredsOpen, setParentCredsOpen] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  
  // Add number state
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [numberSearch, setNumberSearch] = useState({
    country: 'US',
    area_code: '',
    contains: ''
  });
  
  // Import existing numbers state
  const [showImportModal, setShowImportModal] = useState(false);
  const [existingTwilioNumbers, setExistingTwilioNumbers] = useState([]);
  const [loadingExistingNumbers, setLoadingExistingNumbers] = useState(false);
  const [selectedNumbersToImport, setSelectedNumbersToImport] = useState([]);
  const [importingNumbers, setImportingNumbers] = useState(false);
  
  // Assign agent state
  const [showAssignModal, setShowAssignModal] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [showMenuFor, setShowMenuFor] = useState(null);

  // Theme classes
  const cardBg = isDarkMode ? 'bg-[#0c0d12]/90' : 'bg-white/90';
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const bgMain = isDarkMode ? 'bg-[#08090d]' : 'bg-gray-50';
  const inputBg = isDarkMode ? 'bg-white/5' : 'bg-gray-50';

  useEffect(() => {
    fetchData();
  }, [subAccountId]);

  // Auto-refresh balances every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (twilioConnected) {
        api.get(`/api/sub-accounts/${subAccountId}/telephony/twilio/balance`).then(r => setTwilioBalance(r.data)).catch(() => {});
      }
      if (telnyxConnected) {
        api.get(`/api/sub-accounts/${subAccountId}/telephony/telnyx/balance`).then(r => setTelnyxBalance(r.data)).catch(() => {});
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [subAccountId, twilioConnected, telnyxConnected, api]);

  // Close provider menu on outside click
  useEffect(() => {
    if (!providerMenu) return;
    const handleClick = () => setProviderMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [providerMenu]);

  const fetchData = async () => {
    try {
      const [numbersRes, agentsRes, twilioRes, telnyxRes] = await Promise.all([
        api.get(`/api/sub-accounts/${subAccountId}/phone-numbers`).catch(() => ({ data: [] })),
        api.get(`/api/sub-accounts/${subAccountId}/agents`).catch(() => ({ data: [] })),
        api.get(`/api/sub-accounts/${subAccountId}/telephony/twilio/status`).catch(() => ({ data: { connected: false } })),
        api.get(`/api/sub-accounts/${subAccountId}/telephony/telnyx/status`).catch(() => ({ data: { connected: false } }))
      ]);
      
      setPhoneNumbers(numbersRes.data || []);
      setAgents(agentsRes.data || []);
      setTwilioConnected(twilioRes.data?.connected || false);
      setTelnyxConnected(telnyxRes.data?.connected || false);
      
      // Fetch balances for connected providers (silently)
      if (twilioRes.data?.connected) {
        api.get(`/api/sub-accounts/${subAccountId}/telephony/twilio/balance`).then(r => setTwilioBalance(r.data)).catch(() => {});
      }
      if (telnyxRes.data?.connected) {
        api.get(`/api/sub-accounts/${subAccountId}/telephony/telnyx/balance`).then(r => setTelnyxBalance(r.data)).catch(() => {});
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectProvider = (provider) => {
    // Open the type-CONFIRM modal instead of using window.confirm so the user
    // sees exactly how many numbers will be removed and has to actively
    // confirm the destructive action.
    setProviderMenu(null);
    setDisconnectConfirm({ provider, text: '' });
  };

  const confirmDisconnectProvider = async () => {
    const provider = disconnectConfirm.provider;
    if (!provider) return;
    setDisconnecting(provider);
    try {
      const res = await api.delete(`/api/sub-accounts/${subAccountId}/telephony/${provider}/disconnect`);
      const removed = res?.data?.numbers_deleted ?? 0;
      toast.success(
        `${provider === 'twilio' ? 'Twilio' : 'Telnyx'} disconnected${removed ? ` — ${removed} number${removed === 1 ? '' : 's'} removed` : ''}`
      );
      if (provider === 'twilio') { setTwilioConnected(false); setTwilioBalance(null); }
      else { setTelnyxConnected(false); setTelnyxBalance(null); }
      setDisconnectConfirm({ provider: null, text: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to disconnect');
    } finally {
      setDisconnecting(null);
    }
  };

  const handleReconnectProvider = (provider) => {
    setProviderMenu(null);
    setShowConnectModal(provider);
  };

  const handleCheckBalance = async (provider) => {
    setCheckingBalance(provider);
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/telephony/${provider}/balance`);
      if (provider === 'twilio') setTwilioBalance(res.data);
      else setTelnyxBalance(res.data);
      const bal = res.data.balance;
      if (bal !== null && bal !== undefined) {
        toast.success(`${provider === 'twilio' ? 'Twilio' : 'Telnyx'} balance: $${bal.toFixed(2)}`);
      } else {
        toast.error('Could not fetch balance');
      }
    } catch {
      toast.error('Could not fetch balance');
    } finally {
      setCheckingBalance(null);
    }
  };

  const lowBalanceThreshold = 5;
  const twilioBalanceVal = twilioBalance?.balance;
  const telnyxBalanceVal = telnyxBalance?.balance;
  const hasZeroBalance = (twilioBalanceVal !== null && twilioBalanceVal !== undefined && twilioBalanceVal <= 0) ||
    (telnyxBalanceVal !== null && telnyxBalanceVal !== undefined && telnyxBalanceVal <= 0);
  const hasAuthError = (twilioBalance?.error && (twilioBalance.error.includes('401') || twilioBalance.error.includes('Authenticate'))) ||
    (telnyxBalance?.error && (telnyxBalance.error.includes('401') || telnyxBalance.error.includes('Authenticate')));
  const hasLowBalance = !hasZeroBalance && !hasAuthError && (
    (twilioBalanceVal !== null && twilioBalanceVal !== undefined && twilioBalanceVal < lowBalanceThreshold) ||
    (telnyxBalanceVal !== null && telnyxBalanceVal !== undefined && telnyxBalanceVal < lowBalanceThreshold)
  );
  const showBalanceBanner = (hasZeroBalance || hasLowBalance || hasAuthError) && !dismissedBanner;
  const lowBalanceProvider = (twilioBalance?.error || twilioBalanceVal <= 0 || twilioBalanceVal < lowBalanceThreshold) ? 'Twilio' : 'Telnyx';

  const handleDismissBanner = () => {
    localStorage.setItem('vox_balance_banner_dismissed', Date.now().toString());
    setDismissedBanner(true);
  };

  const handleConnectProvider = async () => {
    const provider = showConnectModal;
    if (!connectForm.account_sid || !connectForm.auth_token) {
      toast.error('Account SID and Auth Token are required');
      return;
    }
    
    setIsConnecting(true);
    try {
      const payload = {
        account_sid: connectForm.account_sid,
        auth_token: connectForm.auth_token,
        api_key: connectForm.api_key || undefined,
      };
      // Only Twilio supports the parent-creds dance. Telnyx doesn't have
      // a hierarchical account model.
      if (provider === 'twilio') {
        payload.parent_account_sid = connectForm.parent_account_sid || undefined;
        payload.parent_auth_token = connectForm.parent_auth_token || undefined;
      }
      const res = await api.post(`/api/sub-accounts/${subAccountId}/telephony/${provider}/connect`, payload);

      // OPTION C — auto-open parent creds section when subaccount detected
      // and no parent creds were supplied. We keep the modal open so the
      // user can fill them in immediately.
      if (provider === 'twilio'
          && res?.data?.is_subaccount
          && !res?.data?.has_parent_creds) {
        setParentCredsOpen(true);
        toast.success(
          'Subaccount detected — connection saved. Add parent creds below to display real balance, or skip to use "Parent Creds" mode.',
          { duration: 6000 }
        );
        setTwilioConnected(true);
        fetchData();
        return;  // leave the modal open
      }

      toast.success(`${provider === 'twilio' ? 'Twilio' : 'Telnyx'} connected successfully`);
      setShowConnectModal(null);
      setConnectForm({ account_sid: '', auth_token: '', api_key: '', parent_account_sid: '', parent_auth_token: '' });
      setParentCredsOpen(false);

      if (provider === 'twilio') setTwilioConnected(true);
      else setTelnyxConnected(true);

      // Refresh phone numbers
      fetchData();
    } catch (error) {
      toast.error(`Failed to connect ${provider}`);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleSearchNumbers = async () => {
    if (!twilioConnected && !telnyxConnected) {
      toast.error('Please connect Twilio or Telnyx first');
      return;
    }
    
    setSearchingNumbers(true);
    try {
      const response = await api.post(`/api/sub-accounts/${subAccountId}/phone-numbers/search`, {
        country: numberSearch.country,
        area_code: numberSearch.area_code || undefined,
        contains: numberSearch.contains || undefined,
        provider: twilioConnected ? 'twilio' : 'telnyx'
      });
      
      setAvailableNumbers(response.data?.numbers || []);
      if (response.data?.numbers?.length === 0) {
        toast.info('No numbers found matching your criteria');
      }
    } catch (error) {
      toast.error('Failed to search numbers');
      setAvailableNumbers([]);
    } finally {
      setSearchingNumbers(false);
    }
  };

  const handlePurchaseNumber = async (phoneNumber) => {
    try {
      await api.post(`/api/sub-accounts/${subAccountId}/phone-numbers/purchase`, {
        phone_number: phoneNumber,
        provider: twilioConnected ? 'twilio' : 'telnyx'
      });
      
      toast.success('Phone number purchased successfully');
      setShowAddModal(false);
      setAvailableNumbers([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to purchase number');
    }
  };

  const [sipTrunkBlock, setSipTrunkBlock] = useState(null);
  const handleAssignAgent = async () => {
    if (!selectedAgent) {
      toast.error('Please select an agent');
      return;
    }
    
    try {
      await api.put(`/api/sub-accounts/${subAccountId}/phone-numbers/${showAssignModal}`, {
        agent_id: selectedAgent
      });
      
      toast.success('Agent assigned successfully');
      setShowAssignModal(null);
      setSelectedAgent('');
      fetchData();
    } catch (error) {
      // Backend returns 409 with a structured payload when the underlying
      // Twilio number is bound to a SIP trunk — surface as a migration
      // modal with the exact detach steps instead of a generic toast.
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail && detail.error === 'sip_trunk_bound') {
        setSipTrunkBlock(detail);
      } else {
        toast.error(detail?.message || detail || 'Failed to assign agent');
      }
    }
  };

  // Import provider state
  const [importProvider, setImportProvider] = useState(null);
  
  const handleFetchExistingNumbers = async (provider) => {
    setLoadingExistingNumbers(true);
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/phone-numbers/existing`, {
        params: { provider }
      });
      setExistingTwilioNumbers(response.data?.numbers || []);
      if (response.data?.numbers?.length === 0) {
        toast.info(`No phone numbers found in your ${provider === 'twilio' ? 'Twilio' : 'Telnyx'} account`);
      }
    } catch (error) {
      toast.error(`Failed to fetch ${provider} numbers: ${error.response?.data?.detail || 'Unknown error'}`);
      setExistingTwilioNumbers([]);
    } finally {
      setLoadingExistingNumbers(false);
    }
  };

  const handleToggleNumberSelection = (phoneNumber) => {
    setSelectedNumbersToImport(prev => 
      prev.includes(phoneNumber)
        ? prev.filter(n => n !== phoneNumber)
        : [...prev, phoneNumber]
    );
  };

  const handleSelectAllNumbers = () => {
    if (selectedNumbersToImport.length === existingTwilioNumbers.length) {
      setSelectedNumbersToImport([]);
    } else {
      setSelectedNumbersToImport(existingTwilioNumbers.map(n => n.phone_number));
    }
  };

  const handleImportSelectedNumbers = async () => {
    if (selectedNumbersToImport.length === 0) {
      toast.error('Please select at least one number to import');
      return;
    }
    
    setImportingNumbers(true);
    try {
      const provider = importProvider || (twilioConnected ? 'twilio' : 'telnyx');
      const response = await api.post(`/api/sub-accounts/${subAccountId}/phone-numbers/import`, {
        phone_numbers: selectedNumbersToImport,
        provider
      });
      
      const imported = response.data?.imported || 0;
      toast.success(`Successfully imported ${imported} phone number${imported !== 1 ? 's' : ''}`);
      setShowImportModal(false);
      setSelectedNumbersToImport([]);
      setExistingTwilioNumbers([]);
      setImportProvider(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import numbers');
    } finally {
      setImportingNumbers(false);
    }
  };

  const openImportModal = async (provider) => {
    setImportProvider(provider);
    setShowImportModal(true);
    setSelectedNumbersToImport([]);
    setExistingTwilioNumbers([]);
    await handleFetchExistingNumbers(provider);
  };

  // Reactivate phone number
  const handleReactivateNumber = async (phoneId) => {
    try {
      await api.post(`/api/sub-accounts/${subAccountId}/phone-numbers/${phoneId}/reactivate`);
      toast.success('Phone number reactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to reactivate number');
    }
    setShowMenuFor(null);
  };

  // Deactivate phone number (soft delete)
  const handleDeactivateNumber = async (phoneId) => {
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/phone-numbers/${phoneId}`);
      toast.success('Phone number deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to deactivate number');
    }
    setShowMenuFor(null);
  };

  // Hard delete phone number — opens a confirmation modal that highlights
  // whether the number is currently assigned to an agent.
  const handleDeleteNumber = (phoneId) => {
    const number = phoneNumbers.find((n) => n.phone_id === phoneId);
    setShowMenuFor(null);
    if (!number) return;
    setDeleteNumberConfirm(number);
  };

  const confirmDeleteNumber = async () => {
    const number = deleteNumberConfirm;
    if (!number) return;
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/phone-numbers/${number.phone_id}`, { params: { hard_delete: true } });
      toast.success('Phone number permanently deleted');
      setDeleteNumberConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete number');
    }
  };

  const filteredNumbers = phoneNumbers.filter(num =>
    num.phone_number?.includes(searchTerm) ||
    num.friendly_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    num.agent_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className={`min-h-screen ${bgMain} flex items-center justify-center`}>
        <div className="w-12 h-12 border-4 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bgMain} p-6 lg:p-8`} data-testid="phone-numbers-page">
      {/* Ambient Background */}
      {isDarkMode && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-violet-400/5 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-teal-400/5 rounded-full blur-[80px]" />
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className={`text-2xl lg:text-3xl font-display font-semibold ${textPrimary} mb-1`}>
                Phone Numbers
              </h1>
              <p className={textSecondary}>Manage phone numbers for your voice agents</p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              disabled={!twilioConnected && !telnyxConnected}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-teal-500 text-[#08090d] font-medium hover:shadow-lg hover:shadow-teal-400/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="add-number-btn"
            >
              <Plus size={18} />
              Add Phone Number
            </button>
          </div>
        </motion.div>

        {/* Low/No Balance Banner */}
        {showBalanceBanner && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-4 px-4 py-3 rounded-xl flex items-center gap-3 ${
              hasAuthError || hasZeroBalance
                ? 'bg-red-500/10 border border-red-500/20'
                : 'bg-amber-500/10 border border-amber-500/20'
            }`}
            data-testid="balance-warning-banner"
          >
            <AlertTriangle size={16} className={hasAuthError || hasZeroBalance ? 'text-red-400 shrink-0' : 'text-amber-400 shrink-0'} />
            <span className={`text-sm flex-1 ${hasAuthError || hasZeroBalance ? 'text-red-300' : 'text-amber-300'}`}>
              {hasAuthError
                ? `${lowBalanceProvider} account may be suspended or credentials need updating. Check your ${lowBalanceProvider} billing status.`
                : hasZeroBalance
                ? `${lowBalanceProvider} has no balance. Calls and messages will fail until you add funds.`
                : `${lowBalanceProvider} balance is low. Add funds to avoid service interruption.`
              }
            </span>
            <button
              onClick={handleDismissBanner}
              className={`p-1 rounded-md ${hasAuthError || hasZeroBalance ? 'hover:bg-red-500/20 text-red-400' : 'hover:bg-amber-500/20 text-amber-400'} transition-colors`}
              data-testid="dismiss-balance-banner"
            >
              <X size={14} />
            </button>
          </motion.div>
        )}

        {/* Provider Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 overflow-visible"
        >
          {/* Twilio Card */}
          <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-5 ${twilioConnected ? 'border-green-400/30' : ''} relative`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <span className="text-red-500 font-bold text-xl">T</span>
                </div>
                <div>
                  <h3 className={`font-semibold ${textPrimary}`}>Twilio</h3>
                  <p className={`text-xs ${textSecondary}`}>
                    {twilioConnected ? 'Connected' : 'Not connected'}
                  </p>
                </div>
              </div>
              {twilioConnected ? (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-400/10 text-green-400 text-sm">
                  <CheckCircle size={14} />
                  Active
                </div>
              ) : null}
            </div>
            
            <p className={`text-sm ${textSecondary} mb-3`}>
              Connect your Twilio account to import and manage phone numbers for voice calls.
            </p>

            {/* Balance Display */}
            {twilioConnected && (
              <div className={`flex items-center justify-between mb-3 px-3 py-2 rounded-xl ${isDarkMode ? 'bg-white/[0.03]' : 'bg-gray-50'} border ${cardBorder}`} data-testid="twilio-balance-display">
                <div className="flex items-center gap-2">
                  <DollarSign size={14} className={textSecondary} />
                  <span className={`text-xs ${textSecondary}`}>Balance</span>
                </div>
                {twilioBalance?.balance !== null && twilioBalance?.balance !== undefined ? (
                  <span className={`text-sm font-semibold ${twilioBalance.balance <= 0 ? 'text-red-400' : twilioBalance.balance < 5 ? 'text-amber-400' : 'text-green-400'}`}>
                    {twilioBalance.balance <= 0 ? 'No Balance' : `$${twilioBalance.balance.toFixed(2)}`}
                  </span>
                ) : twilioBalance?.balance_label === 'Parent Creds' ? (
                  <span
                    className={`text-sm font-semibold ${textPrimary} flex items-center gap-1`}
                    title="Subaccount in use — balance is managed in the parent Twilio account. Add parent creds in the Connect modal to display real balance."
                    data-testid="twilio-balance-parent-creds-label"
                  >
                    <span>💼</span> Parent Creds
                  </span>
                ) : (
                  <span className={`text-xs ${twilioBalance?.error ? 'text-red-400' : textSecondary}`}>
                    {twilioBalance?.error
                      ? (twilioBalance.error.includes('401') || twilioBalance.error.includes('Authenticate')
                        ? 'Suspended or Auth Issue'
                        : 'Unavailable')
                      : 'Loading...'}
                  </span>
                )}
              </div>
            )}
            
            {twilioConnected ? (
              <div className="flex gap-2">
                <button
                  onClick={() => openImportModal('twilio')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl ${inputBg} border ${cardBorder} text-teal-400 text-sm font-medium hover:bg-teal-400/10 transition-colors`}
                  data-testid="import-twilio-btn"
                >
                  <Download size={16} />
                  Import Numbers
                </button>
                <div className="relative">
                  <button
                    onClick={(e) => { e.stopPropagation(); setProviderMenu(providerMenu === 'twilio' ? null : 'twilio'); }}
                    className={`py-2.5 px-3 rounded-xl ${inputBg} border ${cardBorder} ${textSecondary} hover:bg-white/10 transition-colors`}
                    data-testid="twilio-settings-btn"
                  >
                    <Settings size={16} />
                  </button>
                  {providerMenu === 'twilio' && (
                    <div className={`absolute right-0 bottom-full mb-1 w-48 ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} border ${cardBorder} rounded-xl shadow-2xl z-[60] overflow-hidden`}>
                      <button
                        onClick={() => handleReconnectProvider('twilio')}
                        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm ${textPrimary} ${isDarkMode ? 'hover:bg-white/[0.06]' : 'hover:bg-gray-50'} transition-colors`}
                        data-testid="reconnect-twilio-btn"
                      >
                        <RefreshCw size={14} />
                        Reconnect
                      </button>
                      <button
                        onClick={() => handleDisconnectProvider('twilio')}
                        disabled={disconnecting === 'twilio'}
                        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-400 ${isDarkMode ? 'hover:bg-red-500/10' : 'hover:bg-red-50'} transition-colors`}
                        data-testid="disconnect-twilio-btn"
                      >
                        {disconnecting === 'twilio' ? <Loader2 size={14} className="animate-spin" /> : <Unplug size={14} />}
                        Disconnect
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowConnectModal('twilio')}
                className="w-full py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-medium hover:bg-red-500/20 transition-colors"
                data-testid="connect-twilio-btn"
              >
                Connect Twilio
              </button>
            )}
          </div>

          {/* Telnyx Card */}
          <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-5 ${telnyxConnected ? 'border-green-400/30' : ''} relative`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-teal-400/10 flex items-center justify-center">
                  <span className="text-teal-400 font-bold text-xl">X</span>
                </div>
                <div>
                  <h3 className={`font-semibold ${textPrimary}`}>Telnyx</h3>
                  <p className={`text-xs ${textSecondary}`}>
                    {telnyxConnected ? 'Connected' : 'Not connected'}
                  </p>
                </div>
              </div>
              {telnyxConnected ? (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-400/10 text-green-400 text-sm">
                  <CheckCircle size={14} />
                  Active
                </div>
              ) : null}
            </div>
            
            <p className={`text-sm ${textSecondary} mb-3`}>
              Connect your Telnyx account for high-quality voice calls with competitive pricing.
            </p>

            {/* Balance Display */}
            {telnyxConnected && (
              <div className={`flex items-center justify-between mb-3 px-3 py-2 rounded-xl ${isDarkMode ? 'bg-white/[0.03]' : 'bg-gray-50'} border ${cardBorder}`} data-testid="telnyx-balance-display">
                <div className="flex items-center gap-2">
                  <DollarSign size={14} className={textSecondary} />
                  <span className={`text-xs ${textSecondary}`}>Balance</span>
                </div>
                {telnyxBalance?.balance !== null && telnyxBalance?.balance !== undefined ? (
                  <span className={`text-sm font-semibold ${telnyxBalance.balance < 5 ? 'text-amber-400' : telnyxBalance.balance <= 0 ? 'text-red-400' : 'text-green-400'}`}>
                    ${telnyxBalance.balance.toFixed(2)}
                  </span>
                ) : (
                  <span className={`text-xs ${textSecondary}`}>{telnyxBalance?.error ? 'Error' : 'Loading...'}</span>
                )}
              </div>
            )}
            
            {telnyxConnected ? (
              <div className="flex gap-2">
                <button
                  onClick={() => openImportModal('telnyx')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl ${inputBg} border ${cardBorder} text-teal-400 text-sm font-medium hover:bg-teal-400/10 transition-colors`}
                  data-testid="import-telnyx-btn"
                >
                  <Download size={16} />
                  Import Numbers
                </button>
                <div className="relative">
                  <button
                    onClick={(e) => { e.stopPropagation(); setProviderMenu(providerMenu === 'telnyx' ? null : 'telnyx'); }}
                    className={`py-2.5 px-3 rounded-xl ${inputBg} border ${cardBorder} ${textSecondary} hover:bg-white/10 transition-colors`}
                    data-testid="telnyx-settings-btn"
                  >
                    <Settings size={16} />
                  </button>
                  {providerMenu === 'telnyx' && (
                    <div className={`absolute right-0 bottom-full mb-1 w-48 ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} border ${cardBorder} rounded-xl shadow-2xl z-[60] overflow-hidden`}>
                      <button
                        onClick={() => handleReconnectProvider('telnyx')}
                        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm ${textPrimary} ${isDarkMode ? 'hover:bg-white/[0.06]' : 'hover:bg-gray-50'} transition-colors`}
                        data-testid="reconnect-telnyx-btn"
                      >
                        <RefreshCw size={14} />
                        Reconnect
                      </button>
                      <button
                        onClick={() => handleDisconnectProvider('telnyx')}
                        disabled={disconnecting === 'telnyx'}
                        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-400 ${isDarkMode ? 'hover:bg-red-500/10' : 'hover:bg-red-50'} transition-colors`}
                        data-testid="disconnect-telnyx-btn"
                      >
                        {disconnecting === 'telnyx' ? <Loader2 size={14} className="animate-spin" /> : <Unplug size={14} />}
                        Disconnect
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowConnectModal('telnyx')}
                className="w-full py-2.5 rounded-xl bg-teal-400/10 border border-teal-400/20 text-teal-400 text-sm font-medium hover:bg-teal-400/20 transition-colors"
                data-testid="connect-telnyx-btn"
              >
                Connect Telnyx
              </button>
            )}
          </div>
        </motion.div>

        {/* Search */}
        {phoneNumbers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-4 mb-6`}
          >
            <div className="relative">
              <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${textSecondary}`} />
              <input
                type="text"
                placeholder="Search phone numbers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-9 pr-4 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
              />
            </div>
          </motion.div>
        )}

        {/* Phone Numbers List */}
        {filteredNumbers.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} overflow-visible`}
          >
            <table className="w-full">
              <thead>
                <tr className={`border-b ${cardBorder} ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <th className={`px-6 py-4 text-left text-xs font-medium ${textSecondary} uppercase`}>Phone Number</th>
                  <th className={`px-6 py-4 text-left text-xs font-medium ${textSecondary} uppercase`}>Provider</th>
                  <th className={`px-6 py-4 text-left text-xs font-medium ${textSecondary} uppercase`}>Assigned Agent</th>
                  <th className={`px-6 py-4 text-left text-xs font-medium ${textSecondary} uppercase`}>Capabilities</th>
                  <th className={`px-6 py-4 text-left text-xs font-medium ${textSecondary} uppercase`}>Status</th>
                  <th className={`px-6 py-4 text-right text-xs font-medium ${textSecondary} uppercase`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredNumbers.map((number, idx) => (
                  <tr key={number.phone_id || idx} className={`border-b ${cardBorder} hover:bg-white/5 transition-colors`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-violet-400/10 flex items-center justify-center">
                          <Phone className="text-violet-400" size={18} />
                        </div>
                        <div>
                          <p className={`font-medium ${textPrimary}`}>{number.phone_number}</p>
                          <p className={`text-xs ${textSecondary}`}>{number.friendly_name || number.phone_number}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                        number.provider === 'twilio'
                          ? 'bg-red-400/10 text-red-400'
                          : 'bg-teal-400/10 text-teal-400'
                      }`}>
                        {number.provider || 'Twilio'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {number.agent_id ? (
                        <div className="flex items-center gap-2" data-testid="phone-assigned-agent">
                          <Bot size={14} className="text-teal-400" />
                          <span className={textPrimary}>{number.agent_name || 'Unknown Agent'}</span>
                        </div>
                      ) : number.routing_flow_id ? (
                        // Number is bound to a Call Routing flow. Show that
                        // explicitly so users don't think it's unassigned —
                        // matches the "auto-update assigned column when on a
                        // flow" gap the user flagged.
                        <button
                          onClick={() => navigate(`/workspace/${workspaceId}/sub-account/${subAccountId}/call-routing/${number.routing_flow_id}/canvas`)}
                          data-testid="phone-assigned-flow"
                          className="flex items-center gap-2 group"
                          title="Open the call routing flow this number is bound to"
                        >
                          <span className="text-base">🔀</span>
                          <span className="text-violet-400 group-hover:underline">
                            {number.routing_flow_name || 'Call Routing Flow'}
                          </span>
                        </button>
                      ) : (
                        <button
                          onClick={() => setShowAssignModal(number.phone_id)}
                          className="text-sm text-teal-400 hover:underline"
                        >
                          Assign Agent
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {number.capabilities?.voice && (
                          <span className={`p-1.5 rounded-md ${inputBg}`} title="Voice">
                            <Mic size={14} className={textSecondary} />
                          </span>
                        )}
                        {number.capabilities?.sms && (
                          <span className={`p-1.5 rounded-md ${inputBg}`} title="SMS">
                            <MessageSquare size={14} className={textSecondary} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`flex items-center gap-1.5 text-xs ${
                        number.status === 'active' ? 'text-green-400' : textSecondary
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          number.status === 'active' ? 'bg-green-400 animate-pulse' : 'bg-gray-500'
                        }`} />
                        {number.status || 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="relative inline-block">
                        <button
                          onClick={() => setShowMenuFor(showMenuFor === number.phone_id ? null : number.phone_id)}
                          className={`p-2 rounded-lg ${textSecondary} hover:bg-white/10 transition-colors`}
                        >
                          <MoreVertical size={16} />
                        </button>
                        
                        {showMenuFor === number.phone_id && (
                          <div className={`absolute right-0 top-full mt-1 w-44 ${isDarkMode ? 'bg-space-800' : 'bg-white'} border ${cardBorder} rounded-xl shadow-2xl z-50`}>
                            <div className="p-1">
                              <button
                                onClick={() => { setShowAssignModal(number.phone_id); setShowMenuFor(null); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${textPrimary} hover:bg-white/10 rounded-lg transition-colors`}
                              >
                                <Bot size={14} /> {number.agent_id ? 'Change Agent' : 'Assign Agent'}
                              </button>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(number.phone_number);
                                  toast.success('Number copied');
                                  setShowMenuFor(null);
                                }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${textPrimary} hover:bg-white/10 rounded-lg transition-colors`}
                              >
                                <Copy size={14} /> Copy Number
                              </button>
                              <hr className={`my-1 ${cardBorder}`} />
                              {number.status === 'inactive' ? (
                                <button
                                  onClick={() => handleReactivateNumber(number.phone_id)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-green-400 hover:bg-green-400/10 rounded-lg transition-colors"
                                  data-testid={`reactivate-${number.phone_id}`}
                                >
                                  <CheckCircle size={14} /> Re-activate
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleDeactivateNumber(number.phone_id)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-400 hover:bg-amber-400/10 rounded-lg transition-colors"
                                  data-testid={`deactivate-${number.phone_id}`}
                                >
                                  <XCircle size={14} /> Deactivate
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteNumber(number.phone_id)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                                data-testid={`delete-${number.phone_id}`}
                              >
                                <Trash2 size={14} /> Delete Permanently
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        ) : (twilioConnected || telnyxConnected) ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-16 text-center`}
          >
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-400/10 to-teal-400/10 flex items-center justify-center mx-auto mb-6">
              <Phone className={isDarkMode ? 'text-gray-600' : 'text-gray-400'} size={40} />
            </div>
            <h3 className={`text-xl font-semibold ${textPrimary} mb-2`}>No Phone Numbers Yet</h3>
            <p className={`${textSecondary} mb-8 max-w-md mx-auto`}>
              Add your first phone number to start receiving and making voice calls with your AI agents.
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-teal-400 to-violet-500 text-white font-medium hover:shadow-lg hover:shadow-teal-400/25 transition-all"
            >
              <Plus size={18} />
              Add Phone Number
            </button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-16 text-center`}
          >
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-400/10 to-orange-400/10 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="text-amber-400" size={40} />
            </div>
            <h3 className={`text-xl font-semibold ${textPrimary} mb-2`}>Connect a Provider First</h3>
            <p className={`${textSecondary} mb-8 max-w-md mx-auto`}>
              Connect your Twilio or Telnyx account above to start managing phone numbers for your AI agents.
            </p>
          </motion.div>
        )}
      </div>

      {/* Connect Provider Modal */}
      <AnimatePresence>
        {showConnectModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setShowConnectModal(null); setConnectForm({ account_sid: '', auth_token: '', api_key: '', parent_account_sid: '', parent_auth_token: '' }); setParentCredsOpen(false); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`w-full max-w-lg ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} rounded-2xl overflow-hidden border ${cardBorder} shadow-2xl`}
              onClick={e => e.stopPropagation()}
            >
              <div className={`p-6 border-b ${cardBorder}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      showConnectModal === 'twilio' ? 'bg-red-500/10' : 'bg-teal-400/10'
                    }`}>
                      <span className={`font-bold text-xl ${showConnectModal === 'twilio' ? 'text-red-500' : 'text-teal-400'}`}>
                        {showConnectModal === 'twilio' ? 'T' : 'X'}
                      </span>
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${textPrimary}`}>
                        Connect {showConnectModal === 'twilio' ? 'Twilio' : 'Telnyx'}
                      </h3>
                      <p className={`text-sm ${textSecondary}`}>Enter your API credentials</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowConnectModal(null); setConnectForm({ account_sid: '', auth_token: '', api_key: '', parent_account_sid: '', parent_auth_token: '' }); setParentCredsOpen(false); }}
                    className={`p-2 rounded-lg ${textSecondary} hover:bg-white/10`}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className={`block text-sm ${textSecondary} mb-2`}>
                    {showConnectModal === 'twilio' ? 'Account SID' : 'API Key ID'}
                  </label>
                  <input
                    type="text"
                    value={connectForm.account_sid}
                    onChange={(e) => setConnectForm({ ...connectForm, account_sid: e.target.value })}
                    placeholder={showConnectModal === 'twilio' ? 'ACxxxxxxxxxxxxxxxx' : 'KEY...'}
                    className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
                  />
                </div>

                <div>
                  <label className={`block text-sm ${textSecondary} mb-2`}>
                    {showConnectModal === 'twilio' ? 'Auth Token' : 'API Key Secret'}
                  </label>
                  <input
                    type="password"
                    value={connectForm.auth_token}
                    onChange={(e) => setConnectForm({ ...connectForm, auth_token: e.target.value })}
                    placeholder="••••••••••••••••"
                    className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
                  />
                </div>

                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-amber-400/5' : 'bg-amber-50'} border border-amber-400/20`}>
                  <p className={`text-sm ${textSecondary}`}>
                    <span className="text-amber-400 font-medium">Where to find these?</span>
                    {' '}Go to your {showConnectModal === 'twilio' ? 'Twilio Console' : 'Telnyx Portal'} → Account Settings → API Keys
                  </p>
                </div>

                {/* Twilio-only: Optional Parent Account Credentials.
                    Auto-opens when backend detects a subaccount on first connect (Option C). */}
                {showConnectModal === 'twilio' && (
                  <div className={`rounded-xl border ${cardBorder} overflow-hidden`}>
                    <button
                      type="button"
                      onClick={() => setParentCredsOpen(!parentCredsOpen)}
                      data-testid="twilio-advanced-parent-creds-toggle"
                      className={`w-full px-4 py-3 flex items-center justify-between ${textPrimary} hover:bg-white/5 transition-colors`}
                    >
                      <span className="text-sm font-medium flex items-center gap-2">
                        <span className="text-base">⚙️</span>
                        Advanced — Parent Account Credentials (Optional)
                      </span>
                      <span className={`text-xs ${textSecondary}`}>{parentCredsOpen ? '▲' : '▼'}</span>
                    </button>
                    {parentCredsOpen && (
                      <div className={`p-4 space-y-3 border-t ${cardBorder} ${isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${textSecondary} leading-relaxed`}>
                          Twilio subaccounts share their parent's wallet — Twilio's API can't read the shared balance from a subaccount's credentials.
                          Add your <span className={`font-medium ${textPrimary}`}>master account</span> SID + Auth Token here to display real-time balance.
                          Leave blank to keep balance private (will show <span className={`font-medium ${textPrimary}`}>"Parent Creds"</span> instead — useful for agencies managing billing privately).
                        </p>
                        <div>
                          <label className={`block text-xs ${textSecondary} mb-1`}>Parent Account SID</label>
                          <input
                            type="text"
                            value={connectForm.parent_account_sid}
                            onChange={(e) => setConnectForm({ ...connectForm, parent_account_sid: e.target.value })}
                            placeholder="ACxxxxxxxxxxxxxxxx"
                            data-testid="twilio-parent-account-sid-input"
                            className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
                          />
                        </div>
                        <div>
                          <label className={`block text-xs ${textSecondary} mb-1`}>Parent Auth Token</label>
                          <input
                            type="password"
                            value={connectForm.parent_auth_token}
                            onChange={(e) => setConnectForm({ ...connectForm, parent_auth_token: e.target.value })}
                            placeholder="••••••••••••••••"
                            data-testid="twilio-parent-auth-token-input"
                            className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className={`p-6 border-t ${cardBorder} flex gap-3`}>
                <button
                  onClick={() => { setShowConnectModal(null); setConnectForm({ account_sid: '', auth_token: '', api_key: '', parent_account_sid: '', parent_auth_token: '' }); setParentCredsOpen(false); }}
                  className={`flex-1 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} hover:bg-white/10 transition-colors`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConnectProvider}
                  disabled={isConnecting}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
                    showConnectModal === 'twilio'
                      ? 'bg-red-500 text-white hover:bg-red-600'
                      : 'bg-teal-400 text-[#08090d] hover:bg-teal-300'
                  } disabled:opacity-50`}
                >
                  {isConnecting ? (
                    <><Loader2 size={18} className="animate-spin" /> Connecting...</>
                  ) : (
                    <><Link2 size={18} /> Connect</>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Number Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setShowAddModal(false); setAvailableNumbers([]); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`w-full max-w-2xl ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} rounded-2xl overflow-hidden border ${cardBorder} shadow-2xl max-h-[90vh] overflow-y-auto`}
              onClick={e => e.stopPropagation()}
            >
              <div className={`p-6 border-b ${cardBorder} sticky top-0 ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} z-10`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-violet-500 flex items-center justify-center">
                      <Phone className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${textPrimary}`}>Add Phone Number</h3>
                      <p className={`text-sm ${textSecondary}`}>Search and purchase a new number</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowAddModal(false); setAvailableNumbers([]); }}
                    className={`p-2 rounded-lg ${textSecondary} hover:bg-white/10`}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div>
                    <label className={`block text-sm ${textSecondary} mb-2`}>Country</label>
                    <select
                      value={numberSearch.country}
                      onChange={(e) => setNumberSearch({ ...numberSearch, country: e.target.value })}
                      className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50`}
                    >
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                      <option value="GB">United Kingdom</option>
                      <option value="AU">Australia</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm ${textSecondary} mb-2`}>Area Code</label>
                    <input
                      type="text"
                      value={numberSearch.area_code}
                      onChange={(e) => setNumberSearch({ ...numberSearch, area_code: e.target.value })}
                      placeholder="e.g., 415"
                      className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm ${textSecondary} mb-2`}>Contains</label>
                    <input
                      type="text"
                      value={numberSearch.contains}
                      onChange={(e) => setNumberSearch({ ...numberSearch, contains: e.target.value })}
                      placeholder="e.g., 1234"
                      className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
                    />
                  </div>
                </div>

                <button
                  onClick={handleSearchNumbers}
                  disabled={searchingNumbers}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-teal-400/10 border border-teal-400/20 text-teal-400 font-medium hover:bg-teal-400/20 transition-colors disabled:opacity-50"
                >
                  {searchingNumbers ? (
                    <><Loader2 size={18} className="animate-spin" /> Searching...</>
                  ) : (
                    <><Search size={18} /> Search Available Numbers</>
                  )}
                </button>

                {availableNumbers.length > 0 && (
                  <div className="mt-6">
                    <p className={`text-sm ${textSecondary} mb-3`}>
                      {availableNumbers.length} numbers found
                    </p>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {availableNumbers.map((num, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center justify-between p-4 rounded-xl ${inputBg} border ${cardBorder} hover:border-teal-400/30 transition-colors`}
                        >
                          <div>
                            <p className={`font-medium ${textPrimary}`}>{num.phone_number}</p>
                            <p className={`text-xs ${textSecondary}`}>
                              {num.locality || num.region || num.iso_country}
                            </p>
                          </div>
                          <button
                            onClick={() => handlePurchaseNumber(num.phone_number)}
                            className="px-4 py-2 rounded-lg bg-teal-400 text-[#08090d] text-sm font-medium hover:bg-teal-300 transition-colors"
                          >
                            Purchase
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SIP Trunk migration modal — shown when backend 409s on assign
          because the Twilio number is bound to a SIP trunk that would
          bypass Voxinity entirely. Educational + actionable. */}
      <AnimatePresence>
        {sipTrunkBlock && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
            onClick={() => setSipTrunkBlock(null)}
            data-testid="sip-trunk-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 28 }}
              className="bg-white dark:bg-zinc-900 border border-rose-400/40 rounded-2xl max-w-lg w-full p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-rose-500/15 flex items-center justify-center flex-shrink-0">
                  <span className="text-rose-500 text-xl">⚠</span>
                </div>
                <div className="flex-1">
                  <div className="text-base font-bold text-zinc-900 dark:text-white">{sipTrunkBlock.title || 'SIP trunk is hijacking this number'}</div>
                  <div className="text-xs mt-0.5 text-zinc-500 dark:text-zinc-400">Why your call kept ringing without an answer</div>
                </div>
              </div>
              <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-200 mb-4">{sipTrunkBlock.message}</p>
              <div className="rounded-xl bg-amber-400/10 border border-amber-400/30 p-3 mb-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-200 mb-2">How to detach</div>
                <ol className="space-y-1.5 text-[12px] text-amber-800 dark:text-amber-100 list-decimal list-inside">
                  {(sipTrunkBlock.fix_steps || []).map((step, i) => <li key={i}>{step}</li>)}
                </ol>
              </div>
              <div className="flex items-center gap-2 justify-end">
                {sipTrunkBlock.docs_url && (
                  <a href={sipTrunkBlock.docs_url} target="_blank" rel="noreferrer"
                    className="text-[11px] font-medium text-sky-600 dark:text-sky-300 hover:underline mr-auto">
                    Twilio SIP Trunking docs ↗
                  </a>
                )}
                <button
                  onClick={() => setSipTrunkBlock(null)}
                  className="px-4 py-2 text-xs font-semibold rounded-lg bg-zinc-200 dark:bg-zinc-800 text-zinc-900 dark:text-white hover:opacity-90"
                  data-testid="sip-trunk-modal-close"
                >Got it</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Assign Agent Modal */}
      <AnimatePresence>
        {showAssignModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setShowAssignModal(null); setSelectedAgent(''); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`w-full max-w-md ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} rounded-2xl overflow-hidden border ${cardBorder} shadow-2xl`}
              onClick={e => e.stopPropagation()}
            >
              <div className={`p-6 border-b ${cardBorder}`}>
                <div className="flex items-center justify-between">
                  <h3 className={`text-lg font-semibold ${textPrimary}`}>Assign Agent</h3>
                  <button
                    onClick={() => { setShowAssignModal(null); setSelectedAgent(''); }}
                    className={`p-2 rounded-lg ${textSecondary} hover:bg-white/10`}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <label className={`block text-sm ${textSecondary} mb-2`}>Select Voice Agent</label>
                <select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} focus:outline-none focus:border-teal-400/50`}
                >
                  <option value="">Select an agent...</option>
                  {agents.filter(a => a.type === 'voice').map(agent => (
                    <option key={agent.agent_id} value={agent.agent_id}>
                      {agent.name} ({agent.direction})
                    </option>
                  ))}
                </select>
              </div>

              <div className={`p-6 border-t ${cardBorder} flex gap-3`}>
                <button
                  onClick={() => { setShowAssignModal(null); setSelectedAgent(''); }}
                  className={`flex-1 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} hover:bg-white/10 transition-colors`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignAgent}
                  disabled={!selectedAgent}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-teal-400 text-[#08090d] font-medium hover:bg-teal-300 transition-colors disabled:opacity-50"
                >
                  <Bot size={18} />
                  Assign Agent
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Import Existing Numbers Modal */}
      <AnimatePresence>
        {showImportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setShowImportModal(false); setSelectedNumbersToImport([]); setExistingTwilioNumbers([]); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`w-full max-w-2xl ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} rounded-2xl overflow-hidden border ${cardBorder} shadow-2xl max-h-[90vh] flex flex-col`}
              onClick={e => e.stopPropagation()}
            >
              <div className={`p-6 border-b ${cardBorder} shrink-0`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      importProvider === 'telnyx' ? 'bg-gradient-to-br from-teal-500 to-cyan-500' : 'bg-gradient-to-br from-red-500 to-pink-500'
                    }`}>
                      <Download className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${textPrimary}`}>Import from {importProvider === 'telnyx' ? 'Telnyx' : 'Twilio'}</h3>
                      <p className={`text-sm ${textSecondary}`}>Select numbers from your {importProvider === 'telnyx' ? 'Telnyx' : 'Twilio'} account to import</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowImportModal(false); setSelectedNumbersToImport([]); setExistingTwilioNumbers([]); }}
                    className={`p-2 rounded-lg ${textSecondary} hover:bg-white/10`}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6 flex-1 overflow-y-auto">
                {loadingExistingNumbers ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 size={32} className="text-teal-400 animate-spin mb-4" />
                    <p className={textSecondary}>Fetching your Twilio phone numbers...</p>
                  </div>
                ) : existingTwilioNumbers.length > 0 ? (
                  <>
                    {/* Select All Header */}
                    <div className={`flex items-center justify-between p-3 rounded-xl ${inputBg} border ${cardBorder} mb-4`}>
                      <button
                        onClick={handleSelectAllNumbers}
                        className={`flex items-center gap-2 ${textPrimary} hover:text-teal-400 transition-colors`}
                      >
                        {selectedNumbersToImport.length === existingTwilioNumbers.length ? (
                          <CheckSquare size={20} className="text-teal-400" />
                        ) : (
                          <Square size={20} />
                        )}
                        <span className="text-sm font-medium">
                          {selectedNumbersToImport.length === existingTwilioNumbers.length ? 'Deselect All' : 'Select All'}
                        </span>
                      </button>
                      <span className={`text-sm ${textSecondary}`}>
                        {selectedNumbersToImport.length} of {existingTwilioNumbers.length} selected
                      </span>
                    </div>

                    {/* Numbers List */}
                    <div className="space-y-2">
                      {existingTwilioNumbers.map((num, idx) => {
                        const isSelected = selectedNumbersToImport.includes(num.phone_number);
                        const isAlreadyImported = phoneNumbers.some(p => p.phone_number === num.phone_number);
                        
                        return (
                          <div
                            key={idx}
                            onClick={() => !isAlreadyImported && handleToggleNumberSelection(num.phone_number)}
                            className={`flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer ${
                              isAlreadyImported 
                                ? `${inputBg} ${cardBorder} opacity-50 cursor-not-allowed`
                                : isSelected
                                  ? 'bg-teal-400/10 border-teal-400/30'
                                  : `${inputBg} ${cardBorder} hover:border-teal-400/20`
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-6 h-6 rounded-md border flex items-center justify-center transition-colors ${
                                isAlreadyImported
                                  ? 'border-gray-500 bg-gray-500/20'
                                  : isSelected
                                    ? 'border-teal-400 bg-teal-400'
                                    : `${cardBorder}`
                              }`}>
                                {(isSelected || isAlreadyImported) && <Check size={14} className={isAlreadyImported ? 'text-gray-400' : 'text-[#08090d]'} />}
                              </div>
                              <div>
                                <p className={`font-medium ${textPrimary}`}>{num.phone_number}</p>
                                <p className={`text-xs ${textSecondary}`}>
                                  {num.friendly_name || num.locality || num.region || 'Phone Number'}
                                  {isAlreadyImported && <span className="ml-2 text-amber-400">(Already imported)</span>}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {num.capabilities?.voice && (
                                <span className={`p-1.5 rounded-md ${inputBg}`} title="Voice">
                                  <Mic size={14} className={textSecondary} />
                                </span>
                              )}
                              {num.capabilities?.sms && (
                                <span className={`p-1.5 rounded-md ${inputBg}`} title="SMS">
                                  <MessageSquare size={14} className={textSecondary} />
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 rounded-2xl bg-amber-400/10 flex items-center justify-center mb-4">
                      <Phone className="text-amber-400" size={28} />
                    </div>
                    <h4 className={`font-semibold ${textPrimary} mb-2`}>No Phone Numbers Found</h4>
                    <p className={`text-sm ${textSecondary} text-center max-w-sm`}>
                      Your Twilio account doesn't have any phone numbers yet. You can purchase new numbers instead.
                    </p>
                    <button
                      onClick={() => { setShowImportModal(false); setShowAddModal(true); }}
                      className="mt-4 px-4 py-2 rounded-lg bg-teal-400/10 text-teal-400 text-sm font-medium hover:bg-teal-400/20 transition-colors"
                    >
                      Purchase New Number
                    </button>
                  </div>
                )}
              </div>

              {existingTwilioNumbers.length > 0 && (
                <div className={`p-6 border-t ${cardBorder} flex gap-3 shrink-0`}>
                  <button
                    onClick={() => { setShowImportModal(false); setSelectedNumbersToImport([]); setExistingTwilioNumbers([]); }}
                    className={`flex-1 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} hover:bg-white/10 transition-colors`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImportSelectedNumbers}
                    disabled={selectedNumbersToImport.length === 0 || importingNumbers}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-teal-400 to-teal-500 text-[#08090d] font-medium hover:shadow-lg hover:shadow-teal-400/25 transition-all disabled:opacity-50"
                  >
                    {importingNumbers ? (
                      <><Loader2 size={18} className="animate-spin" /> Importing...</>
                    ) : (
                      <><Download size={18} /> Import {selectedNumbersToImport.length} Number{selectedNumbersToImport.length !== 1 ? 's' : ''}</>
                    )}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}

        {/* Delete Phone Number Confirmation Modal */}
        {deleteNumberConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
            onClick={() => setDeleteNumberConfirm(null)}
            data-testid="delete-number-confirm-backdrop"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              className={`w-full max-w-md ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} rounded-2xl overflow-hidden border ${cardBorder} shadow-2xl`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`p-6 border-b ${cardBorder} flex items-center gap-3`}>
                <div className="w-11 h-11 rounded-xl bg-red-400/15 border border-red-400/30 flex items-center justify-center">
                  <Trash2 className="text-red-400" size={20} />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${textPrimary}`}>Delete phone number?</h3>
                  <p className={`text-sm ${textSecondary}`}>This action cannot be undone.</p>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className={`p-4 rounded-xl ${inputBg} border ${cardBorder}`}>
                  <div className={`text-xs uppercase tracking-wider ${textSecondary} mb-1`}>Number</div>
                  <div className={`font-medium ${textPrimary}`}>{deleteNumberConfirm.phone_number}</div>
                  {deleteNumberConfirm.friendly_name && deleteNumberConfirm.friendly_name !== deleteNumberConfirm.phone_number && (
                    <div className={`text-xs ${textSecondary} mt-0.5`}>{deleteNumberConfirm.friendly_name}</div>
                  )}
                </div>

                {deleteNumberConfirm.agent_id && (
                  <div className="p-4 rounded-xl bg-amber-400/[0.08] border border-amber-400/30 text-sm flex items-start gap-3" data-testid="delete-number-agent-warning">
                    <Bot className="text-amber-300 mt-0.5 shrink-0" size={18} />
                    <div className="text-amber-100/90">
                      <div className="font-semibold text-amber-200 mb-1">Assigned to an AI agent</div>
                      This number is currently assigned to{' '}
                      <span className="text-white">{deleteNumberConfirm.agent_name || 'an agent'}</span>.
                      Deleting it will detach the number from the agent and stop all inbound calls to it.
                    </div>
                  </div>
                )}

                {deleteNumberConfirm.routing_flow_id && !deleteNumberConfirm.agent_id && (
                  <div className="p-4 rounded-xl bg-amber-400/[0.08] border border-amber-400/30 text-sm flex items-start gap-3">
                    <span className="text-base mt-0.5 shrink-0">🔀</span>
                    <div className="text-amber-100/90">
                      <div className="font-semibold text-amber-200 mb-1">Bound to a Call Routing flow</div>
                      This number is used by the routing flow{' '}
                      <span className="text-white">{deleteNumberConfirm.routing_flow_name || 'this routing flow'}</span>.
                      Deleting it will stop calls from being routed through this flow.
                    </div>
                  </div>
                )}

                <p className={`text-xs ${textSecondary}`}>
                  The number stays in your provider account (Twilio/Telnyx). We just remove it from Voxinity.
                </p>
              </div>
              <div className={`p-6 border-t ${cardBorder} flex gap-3`}>
                <button
                  onClick={() => setDeleteNumberConfirm(null)}
                  className={`flex-1 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} hover:bg-white/10 transition-colors`}
                  data-testid="delete-number-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteNumber}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-medium transition-colors"
                  data-testid="delete-number-confirm"
                >
                  <Trash2 size={16} /> Delete Permanently
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Disconnect Provider Confirmation (type CONFIRM) */}
        {disconnectConfirm.provider && (() => {
          const provider = disconnectConfirm.provider;
          const providerLabel = provider === 'twilio' ? 'Twilio' : 'Telnyx';
          const numbersForProvider = phoneNumbers.filter((n) => (n.provider || 'twilio') === provider);
          const assignedCount = numbersForProvider.filter((n) => n.agent_id || n.routing_flow_id).length;
          const totalCount = numbersForProvider.length;
          const canDisconnect = disconnectConfirm.text === 'CONFIRM' && !disconnecting;
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
              onClick={() => !disconnecting && setDisconnectConfirm({ provider: null, text: '' })}
              data-testid="disconnect-provider-backdrop"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 16 }}
                className={`w-full max-w-lg ${isDarkMode ? 'bg-[#0c0d12]' : 'bg-white'} rounded-2xl overflow-hidden border ${cardBorder} shadow-2xl`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`p-6 border-b ${cardBorder} flex items-center gap-3`}>
                  <div className="w-11 h-11 rounded-xl bg-red-400/15 border border-red-400/30 flex items-center justify-center">
                    <Unplug className="text-red-400" size={20} />
                  </div>
                  <div>
                    <h3 className={`text-lg font-semibold ${textPrimary}`}>Disconnect {providerLabel}?</h3>
                    <p className={`text-sm ${textSecondary}`}>This is a destructive action.</p>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="p-4 rounded-xl bg-red-400/[0.06] border border-red-400/25 text-sm text-red-100/90 space-y-2">
                    <div className="font-semibold text-red-200">What happens when you disconnect:</div>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>
                        <span className="text-white">{totalCount}</span> imported {providerLabel} number{totalCount === 1 ? '' : 's'} will be{' '}
                        <span className="text-white">permanently removed</span> from Voxinity.
                      </li>
                      {assignedCount > 0 && (
                        <li>
                          <span className="text-white">{assignedCount}</span> of those{' '}
                          {assignedCount === 1 ? 'is' : 'are'} currently{' '}
                          <span className="text-white">assigned to an AI agent or routing flow</span> — those bindings will be cleared.
                        </li>
                      )}
                      <li>Your stored {providerLabel} credentials will be erased.</li>
                      <li>
                        The numbers themselves remain in your {providerLabel} account (we only clear our local mirror).
                      </li>
                    </ul>
                  </div>

                  <div>
                    <label className={`block text-xs font-semibold ${textSecondary} uppercase tracking-wider mb-2`}>
                      Type <span className="text-red-300">CONFIRM</span> to disconnect
                    </label>
                    <input
                      type="text"
                      autoFocus
                      value={disconnectConfirm.text}
                      onChange={(e) => setDisconnectConfirm({ ...disconnectConfirm, text: e.target.value })}
                      placeholder="CONFIRM"
                      className={`w-full px-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} placeholder:${textSecondary} focus:outline-none focus:border-red-400/50 font-mono tracking-wider`}
                      data-testid="disconnect-provider-confirm-input"
                    />
                  </div>
                </div>
                <div className={`p-6 border-t ${cardBorder} flex gap-3`}>
                  <button
                    onClick={() => setDisconnectConfirm({ provider: null, text: '' })}
                    disabled={!!disconnecting}
                    className={`flex-1 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} hover:bg-white/10 transition-colors disabled:opacity-50`}
                    data-testid="disconnect-provider-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDisconnectProvider}
                    disabled={!canDisconnect}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-red-500 hover:bg-red-600 disabled:bg-red-500/30 disabled:cursor-not-allowed text-white font-medium transition-colors"
                    data-testid="disconnect-provider-confirm"
                  >
                    {disconnecting ? (
                      <><Loader2 size={16} className="animate-spin" /> Disconnecting...</>
                    ) : (
                      <><Unplug size={16} /> Disconnect {providerLabel}</>
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
};

const PhoneNumbersPage = () => (
  <SubAccountLayout>
    <PhoneNumbersContent />
  </SubAccountLayout>
);

export default PhoneNumbersPage;
